import React, { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { fromJS } from "immutable";
import { useVirtualizer } from "@tanstack/react-virtual";

import GridAction from "../actions/GridAction";
import ProjectAction from "../actions/ProjectAction";
import CustomFieldDialog from "../dialog/CustomFieldDialog";
import { deleteField } from "../api/ProjectApi";
import HeadCellWrapper from "./HeadCellWrapper";
import { ContentTypes, DialogTypes } from "../constants/action-types";
import SelectionField from "./SelectionField";
import TaskAction from "../actions/TaskAction";
import ActivityAction from "../actions/ActivityAction";
import GridColumnSetting from "./GridColumnSetting";
import CellRenderer from "./CellRenderer";
import TableAggregation from "./TableAggregation";
import { useQuery } from "../AppLayout";

import { useGridTableSetup } from "./hooks/useGridTableSetup";
import { useScrollShadow } from "./hooks/useScrollShadow";
import PinnedShadow from "./components/PinnedShadow";
import SkeletonRows from "./components/SkeletonRows";
import GridEmptyState from "./components/GridEmptyState";

// --- Types ---

interface ColumnConfig {
  selector: string;
  name: string;
  width: number;
  align?: string;
  border?: boolean;
  isCustomField?: boolean;
  formType?: string;
  show?: boolean;
  aggregatingFunc?: string;
  [key: string]: any;
}

interface GridTableProps {
  heads: ColumnConfig[];
  data: any; // Immutable Map with "children" key
  project: any; // Immutable Map
  hiddenColumns: any; // Immutable List
  isSettings?: boolean;
  loading?: boolean;
  onChangeFilter: (filter: any) => void;
  onReload: () => void;
  onSortField: (sort: { field: string; direction: string }) => void;
  getSortDirection: (fieldId: string) => string;
  getCurrentSortDirection?: (fieldId: string) => string | null;
  onResizeColumn: (column: any) => void;
  showHideColumn: (column: any) => void;
  currentSize?: { width: number; height: number };
  contentType: string;
}

// --- Constants ---

const ROW_HEIGHT = 40;

// --- Component ---

const GridTable: React.FC<GridTableProps> = ({
  heads,
  data,
  project,
  hiddenColumns,
  isSettings = true,
  loading = false,
  onChangeFilter,
  onReload,
  onSortField,
  getSortDirection,
  getCurrentSortDirection,
  onResizeColumn,
  showHideColumn,
  currentSize = { width: 0, height: 0 },
  contentType,
}) => {
  const dispatch = useDispatch();
  const currentParams = useQuery();
  const projectView = currentParams.get("view");

  // --- Hooks ---

  const { scrollContainerRef, isScrolled, containerWidth } = useScrollShadow();

  const items = useMemo(() => data.get("children"), [data]);
  const rows = useMemo(() => items.toArray(), [items]);

  const { table, getPinnedStyle } = useGridTableSetup({
    heads,
    rows,
    onResizeColumn,
    containerWidth,
  });

  // --- Derived data ---

  // Memoized Immutable column objects for CellRenderer
  const immutableHeaders = useMemo(() => {
    const map = new Map<string, any>();
    heads.forEach((head) => map.set(head.selector, fromJS(head)));
    return map;
  }, [heads]);

  // Head lookup map: selector -> { head config, original index }
  const headMap = useMemo(() => {
    const map = new Map<string, { head: ColumnConfig; index: number }>();
    heads.forEach((head, idx) => map.set(head.selector, { head, index: idx }));
    return map;
  }, [heads]);

  // Skeleton column info (precomputed for SkeletonRows)
  const skeletonColumns = useMemo(() => {
    const headerGroup = table.getHeaderGroups()[0];
    return heads.map((head) => {
      const header = headerGroup?.headers.find((h) => h.id === head.selector);
      const isPinned = header ? !!header.column.getIsPinned() : false;
      return {
        selector: head.selector,
        width: header?.getSize() || head.width || 150,
        isPinned,
        pinnedStyle: header ? getPinnedStyle(header.column, false) : {},
      };
    });
  }, [heads, table, getPinnedStyle]);

  // --- Row virtualizer ---

  const tableRows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0;

  // --- Business logic callbacks ---

  const handleShowAddNewRecord = useCallback(() => {
    if (contentType === ContentTypes.ACTIVITY) {
      dispatch(
        ActivityAction.showActivityDialog(null, null, {
          activityType: "meeting",
          summary: "Meeting",
        })
      );
    } else {
      dispatch(
        TaskAction.showTaskDialog(
          {
            projectId: project.get("id"),
            tenantId: project.get("tenantId"),
            type: contentType,
            projectView,
          },
          contentType
        )
      );
    }
  }, [dispatch, contentType, project, projectView]);

  const isSorting = useCallback(
    (fieldId: string): boolean => {
      const field =
        project
          .get("fields")
          .find(
            (item: any) => item.get("id") === fieldId && item.get("formType") === contentType
          ) || fromJS({});
      return ["string", "datetime", "double", "contact", "account", "stringArray"].includes(
        field.get("type")
      );
    },
    [project, contentType]
  );

  const handleChangeColumnOrder = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      const result = Array.from(heads);
      const [removed] = result.splice(sourceIndex, 1);
      result.splice(destinationIndex, 0, removed);
      dispatch(
        GridAction.changeColumnOrder({
          columns: result,
          projectId: project.get("id"),
          contentType,
        })
      );
    },
    [heads, project, contentType, dispatch]
  );

  // --- Layout helpers ---

  const getContainerHeight = () => {
    const rowHeight = rows.length * ROW_HEIGHT;
    const maxHeight = Math.min(rowHeight, (currentSize.height || 600) - 110);
    return Math.max(maxHeight, 200);
  };

  // --- Render ---

  return (
    <>
      <div className="w-full h-full flex p-2 min-h-0 bg-white">
        {/* Main table container */}
        <div
          ref={scrollContainerRef}
          className="w-full overflow-auto bg-white rounded-md border border-gray-300 flex-1 min-h-0 isolate"
          style={{
            width: (currentSize.width || 0) - 110,
          }}
        >
          <table
            className="table-fixed bg-white rounded-md"
            style={{ width: table.getTotalSize() }}
          >
            {/* ---- Header ---- */}
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="h-10 bg-gray-50 border-b border-gray-300 sticky top-0 z-20"
                >
                  {headerGroup.headers.map((header) => {
                    const info = headMap.get(header.id);
                    if (!info) return null;
                    const { head, index: headIndex } = info;
                    const pinnedStyle = getPinnedStyle(header.column, true);
                    const isPinned = !!header.column.getIsPinned();

                    return (
                      <th
                        key={header.id}
                        scope="col"
                        className={`px-3 py-2 bg-gray-50 text-left text-xs font-medium text-gray-700 leading-4 tracking-tight relative group/th ${
                          headIndex < heads.length - 1 ? "border-r border-r-gray-200" : ""
                        }`}
                        style={{
                          width: header.getSize(),
                          minWidth: header.getSize(),
                          overflow: isPinned ? "visible" : undefined,
                          ...pinnedStyle,
                        }}
                      >
                        {isPinned && <PinnedShadow isScrolled={isScrolled} />}

                        <div className="flex items-center gap-2 h-full">
                          {head.selector === "name" && (
                            <div className="flex items-center">
                              <SelectionField itemId="" header={true} />
                            </div>
                          )}
                          <HeadCellWrapper
                            headerIndex={headIndex}
                            headerCount={heads.length}
                            isCustomField={head.isCustomField}
                            headerData={head}
                            showSorting={isSorting(head.selector)}
                            sortDirection={getSortDirection(head.selector)}
                            currentSortDirection={getCurrentSortDirection?.(head.selector) ?? null}
                            isSettings={isSettings}
                            isPinned={isPinned}
                            onSelectColumn={() => {}}
                            onEditColumn={() => {
                              dispatch(
                                GridAction.showGridDialog(
                                  head.selector,
                                  DialogTypes.updateCustomFieldDialog,
                                  contentType
                                )
                              );
                            }}
                            onHideColumn={() => showHideColumn({ ...head, show: false })}
                            onMoveLeft={() => handleChangeColumnOrder(headIndex, headIndex - 1)}
                            onMoveRight={() => handleChangeColumnOrder(headIndex, headIndex + 1)}
                            onSortField={() =>
                              onSortField({
                                field: `data.${head.selector}.value`,
                                direction: getSortDirection(head.selector),
                              })
                            }
                            iconChevronDown
                          >
                            {head.name}
                          </HeadCellWrapper>
                        </div>

                        {/* Column resize handle */}
                        <div
                          onDoubleClick={() => header.column.resetSize()}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`absolute -right-[2px] top-0 bottom-0 w-[5px] cursor-col-resize select-none touch-none z-10 flex items-center justify-center ${
                            header.column.getIsResizing()
                              ? "opacity-100"
                              : "opacity-0 group-hover/th:opacity-100"
                          }`}
                        >
                          <div
                            className={`w-[2px] h-full transition-colors ${
                              header.column.getIsResizing()
                                ? "bg-blue-500"
                                : "bg-gray-300 hover:bg-blue-500"
                            }`}
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            {/* ---- Body ---- */}
            <tbody className="bg-white">
              {loading ? (
                <SkeletonRows columns={skeletonColumns} />
              ) : (
                <>
                  {paddingTop > 0 && (
                    <tr>
                      <td style={{ height: paddingTop, padding: 0, border: 0 }} />
                    </tr>
                  )}
                  {virtualRows.map((virtualRow) => {
                    const row = tableRows[virtualRow.index];
                    return (
                      <tr
                        key={`item_${row.original.get("id")}`}
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                        className="h-10 border-b border-gray-300"
                      >
                        {row.getVisibleCells().map((cell) => {
                          const pinnedStyle = getPinnedStyle(cell.column, false);
                          const isPinned = !!cell.column.getIsPinned();
                          const immutableCol = immutableHeaders.get(cell.column.id);

                          return (
                            <td
                              key={cell.id}
                              className={`px-3 relative group/cell border-r border-r-gray-200 last:border-r-0 ${
                                isPinned ? "bg-white" : ""
                              }`}
                              style={{
                                width: cell.column.getSize(),
                                minWidth: cell.column.getSize(),
                                overflow: isPinned ? "visible" : undefined,
                                ...pinnedStyle,
                              }}
                            >
                              {isPinned && <PinnedShadow isScrolled={isScrolled} />}
                              <CellRenderer
                                row={row.original}
                                onChangeFilter={onChangeFilter}
                                onReload={onReload}
                                column={immutableCol}
                                showAddRowFilter={true}
                                showSelectable={true}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {paddingBottom > 0 && (
                    <tr>
                      <td
                        style={{
                          height: paddingBottom,
                          padding: 0,
                          border: 0,
                        }}
                      />
                    </tr>
                  )}
                </>
              )}
            </tbody>

            {/* ---- Aggregation footer ---- */}
            <tfoot>
              <TableAggregation
                headers={fromJS(heads)}
                project={project}
                table={table}
                getPinnedStyle={getPinnedStyle}
                isScrolled={isScrolled}
              />
            </tfoot>
          </table>

          {/* Empty state */}
          {data.get("children").size === 0 && !loading && (
            <GridEmptyState onAddRecord={handleShowAddNewRecord} />
          )}
        </div>

        {/* Column picker sidebar */}
        <div className="items-center pr-1 shrink-0" style={{ height: getContainerHeight() }}>
          {isSettings && (
            <GridColumnSetting
              contentType={contentType}
              fields={project.get("fields")}
              hiddenColumns={hiddenColumns}
              onSelectColumn={showHideColumn}
            />
          )}
        </div>
      </div>

      {/* Custom field dialog */}
      <CustomFieldDialog
        onClose={() => dispatch(GridAction.hideTaskDialog())}
        onSubmit={() => {
          dispatch(ProjectAction.getProject(project.get("id")));
          dispatch(GridAction.hideTaskDialog());
        }}
        onDelete={async (customFieldId: string) => {
          try {
            await deleteField(project.get("id"), customFieldId);
          } catch (error: any) {
            // Ignore 500 errors as data is still processed successfully on the backend
            if (error?.response?.status !== 500) {
              throw error;
            }
          }
          dispatch(ProjectAction.getProject(project.get("id")));
          dispatch(GridAction.hideTaskDialog());
        }}
      />
    </>
  );
};

export default GridTable;
